// Web implementation of @scribe/core's UploadQueueStorage — persists queue metadata (NOT
// audio bytes; those stay in-memory in the ArrayBuffer map below, keyed by item id) so a
// page refresh doesn't silently lose in-flight upload bookkeeping. Uses localStorage: this
// is small JSON metadata (never transcript/audio content), well under typical quota.
import type { UploadItem, UploadQueueStorage } from '@scribe/core/upload-queue';

const STORAGE_KEY = 'scribe.upload-queue.v1';

function readAll(): Record<string, UploadItem> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, UploadItem>) : {};
  } catch {
    return {};
  }
}

function writeAll(items: Record<string, UploadItem>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Quota or serialization failure — non-fatal; the in-memory queue driver keeps working
    // for this session, it just won't survive a reload. Never throw from storage.
  }
}

export const webUploadQueueStorage: UploadQueueStorage = {
  async loadAll() {
    return Object.values(readAll());
  },
  async save(item) {
    const all = readAll();
    all[item.id] = item;
    writeAll(all);
  },
  async remove(id) {
    const all = readAll();
    delete all[id];
    writeAll(all);
  },
};

// Separate, purely in-memory registry for the actual audio Blob/File per upload item — kept
// out of the persisted UploadQueueStorage since Blobs aren't JSON-serializable and audio
// content must never be written to localStorage/disk unencrypted (org policy: no raw
// transcript/audio content logged or persisted client-side beyond the upload itself).
const blobRegistry = new Map<string, Blob>();

export function registerUploadBlob(itemId: string, blob: Blob): void {
  blobRegistry.set(itemId, blob);
}

export function getUploadBlob(itemId: string): Blob | undefined {
  return blobRegistry.get(itemId);
}

export function releaseUploadBlob(itemId: string): void {
  blobRegistry.delete(itemId);
}
